/**
 * Failed attempts per key (a nickname, a login, a client address, an operator) in a sliding window, and the attempts
 * under way per key. Memory only: a restart forgets them. Used by every password check of the backend (players:
 * AuthService, operators: OperatorService).
 *
 * Parallel attempts: the caller checks the limit (retryAfter) and reserves the attempt (reserve) in ONE synchronous
 * step before its first await, and releases the reservation (release) when the check is over. The failures plus the
 * attempts under way never exceed the limit, so a burst of parallel requests gets no more checks than a sequence of
 * them would (a check that awaits a password hash would otherwise let every request of the burst pass the limit
 * before the first failure is noted).
 *
 * Bounded memory: beyond `maxKeys` keys the expired counters go first, then the least recently failed ones that are
 * below their limit. A key at its limit (a locked nickname or address) is kept: a spray of failures on other keys must
 * not unlock it. Only when every key is at its limit do the least recently failed ones go.
 */

/** How long to wait when the attempts under way fill the budget (they end within a second or so). */
export const PENDING_RETRY_MS = 1_000;

interface Entry {
  /** Failure times in the window, oldest first. */
  times: number[];
  /** The limit the key was last noted against (eviction keeps keys at it). */
  max: number;
}

export class FailureCounter {
  /** In order of the last failure (a noted key moves to the end). */
  private readonly map = new Map<string, Entry>();
  private readonly pending = new Map<string, number>();
  private readonly windowOf: () => number;

  constructor(
    windowMs: number | (() => number),
    private readonly maxKeys = 50_000,
  ) {
    this.windowOf = typeof windowMs === 'function' ? windowMs : () => windowMs;
  }

  /** Failure times of the key in the window (expired ones are dropped). */
  private recent(key: string, now: number): number[] {
    const e = this.map.get(key);
    if (!e) return [];
    const windowMs = this.windowOf();
    const times = e.times.filter((t) => now - t < windowMs);
    if (times.length === 0) this.map.delete(key);
    else e.times = times;
    return times;
  }

  /** Failures of the key in the window. */
  count(key: string, now: number): number {
    return this.recent(key, now).length;
  }

  /**
   * Milliseconds until an attempt of the key is allowed again, or null when its failures plus the attempts under way
   * stay below `max` (max <= 0: no limit).
   */
  retryAfter(key: string, max: number, now: number): number | null {
    if (!(max > 0)) return null;
    const times = this.recent(key, now);
    if (times.length >= max) return Math.max(1, times[times.length - max] + this.windowOf() - now);
    return times.length + (this.pending.get(key) ?? 0) >= max ? PENDING_RETRY_MS : null;
  }

  /** An attempt of the key is under way (until release). */
  reserve(key: string): void {
    this.pending.set(key, (this.pending.get(key) ?? 0) + 1);
  }

  release(key: string): void {
    const n = (this.pending.get(key) ?? 0) - 1;
    if (n > 0) this.pending.set(key, n);
    else this.pending.delete(key);
  }

  /** A failure of the key now, counted against `max` (its limit, for the eviction rule). */
  note(key: string, now: number, max: number): void {
    const times = this.recent(key, now);
    times.push(now);
    this.map.delete(key);
    this.map.set(key, { times, max });
    if (this.map.size > this.maxKeys) this.prune(now, key);
  }

  /** Forgets the failures of the key (a right password). */
  clear(key: string): void {
    this.map.delete(key);
  }

  /** Keys with failures in memory (tests). */
  get size(): number {
    return this.map.size;
  }

  /** See the module comment. `keep` is the key noted just now. */
  private prune(now: number, keep: string): void {
    const windowMs = this.windowOf();
    for (const [k, e] of this.map) if (e.times.every((t) => now - t >= windowMs)) this.map.delete(k);
    const target = Math.floor(this.maxKeys * 0.9);
    if (this.map.size <= this.maxKeys) return;
    for (const [k, e] of this.map) {
      if (this.map.size <= target) return;
      if (k !== keep && this.recent(k, now).length < e.max) this.map.delete(k);
    }
    for (const k of this.map.keys()) {
      if (this.map.size <= this.maxKeys) return;
      if (k !== keep) this.map.delete(k);
    }
  }
}
